import clsx from "clsx";
import { useAtom } from "jotai";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  currentPageAtom,
  currentQuestionErrorsAtom,
  currentQuestionIndexAtom,
  currentQuestionsAtom,
  questionWithBannerIndexAtom,
  quizAtom,
} from "../../../widget.state";
import { QuizQuestion } from "../../question/question.component";

export function QuizQuestions({ showAds }: { showAds: boolean }) {
  const questionRefs = useRef<HTMLDivElement[]>([]);
  const [quiz] = useAtom(quizAtom);
  const [currentPage] = useAtom(currentPageAtom);
  const [currentQuestions] = useAtom(currentQuestionsAtom);
  const [currentQuestionIndex] = useAtom(currentQuestionIndexAtom);
  const [currentQuestionErrors] = useAtom(currentQuestionErrorsAtom);
  const [questionWithBannerIndex, setQuestionWithBannerIndex] = useAtom(
    questionWithBannerIndexAtom
  );
  const [autoScrollDebounce, setAutoScrollDebounce] = useState<
    _.DebouncedFunc<() => void>
  >();

  const handleScrollToCurrentQuestion = useCallback(() => {
    setQuestionWithBannerIndex(null);
    window.scrollTo({
      top: questionRefs.current[currentQuestionIndex].offsetTop - 100,
      behavior: "smooth",
    });
  }, [currentQuestionIndex, setQuestionWithBannerIndex]);

  const handleDebounceNextQuestion = useCallback(
    (waitTime: number) => {
      setAutoScrollDebounce((prevDebounce) => {
        prevDebounce?.cancel();
        return debounce(handleScrollToCurrentQuestion, waitTime * 1000);
      });
    },
    [handleScrollToCurrentQuestion]
  );

  useEffect(() => {
    setQuestionWithBannerIndex(currentQuestionIndex - 1);
  }, [currentQuestionIndex, setQuestionWithBannerIndex]);

  useEffect(() => {
    if (autoScrollDebounce != undefined) {
      autoScrollDebounce();
    }
  }, [autoScrollDebounce]);

  return (
    <>
      <div
        className={clsx(
          "quiz--single-page__body",
          quiz.isArcade && "quiz--single-page__body--arcade-trivia"
        )}
        data-testid="quiz-questions"
      >
        {currentQuestions.map((question, i) => {
          const quizQuestion = {
            ref: (ref: HTMLDivElement) =>
              (questionRefs.current[question.index] = ref!),
            id: question.id,
            questionIndex: question.index,
            currentQuestionIndex: currentQuestionIndex,
            showQuestionAds: showAds,
            onSetQuestionWithBanner: setQuestionWithBannerIndex,
            onDebounceAutoScroll: handleDebounceNextQuestion,
            hasBanner:
              question.index === questionWithBannerIndex &&
              (currentQuestionIndex < question.index
                ? true
                : i < currentQuestions.length - 1) &&
              !currentQuestionErrors.includes("TAC_NOT_ACCEPTED"),
            renderOptInReminder:
              !quiz.isArcade &&
              window.FKL.allowOptInReminder &&
              (quiz.submissionCount || 1) > 1 &&
              currentPage === 0 &&
              i === 0,
          };

          return <QuizQuestion key={question.id} {...quizQuestion} />;
        })}
      </div>
    </>
  );
}
